import { FunnelStartedFields, TestStartedFields, UserClickFields, ViewContentFields, XyPixel } from '@xylabs/pixel'

import { ProcessPaymentResult } from 'business-logic/cart/checkout'
import { Cart, CartItemSlot, Funnel } from 'business-logic/cart/hooks'
import { AddressInfoData } from 'business-logic/cart/models'
import { ContactInfoWrapper } from 'business-logic/cart/wrappers'
import { CoinAddressInfoComplete, CoinContactInfoComplete, CoinEvents } from './Coin'
import { CoinUserEventHandlerBase } from './CoinUserEventHandlerBase'
import { CustomProperties } from './CustomProperties'
import { TestData } from './TestData'
import { Iterable } from '../Iterable'

interface PurchaseCompletedData extends CustomProperties {
  rid?: string
  txid?: string
}

export class CoinEventHandler<T> extends CoinUserEventHandlerBase<T> {
  private events = new CoinEvents<CustomProperties>()

  async contactInfoComplete() {
    const contactInfo = new ContactInfoWrapper().load()
    const data = contactInfo.getSanitizedData()
    XyPixel.instance.identify(contactInfo.data.email)
    //TODO - refactor

    const cartProperties = this.buildCustomProperties<CoinContactInfoComplete>(data)
    try {
      await Promise.allSettled([
        this.events.contactInfoComplete().send(cartProperties, this.eventId),
        Iterable.identify(),
        Iterable.customEvent('FunnelStarted')
      ])
    } catch (ex) {
      console.error(ex)
    }
  }

  async addressInfoComplete(addressInfo?: AddressInfoData) {
    await this.events.addressInfoComplete().send(this.buildCustomProperties<CoinAddressInfoComplete>({ ...addressInfo }), this.eventId)
  }

  async paymentInfoComplete() {
    await this.events.paymentInfoComplete().send(this.buildCustomProperties(), this.eventId)
  }

  async addToCart() {
    await this.events.addToCart().send(this.buildCartProperties(), this.eventId)
  }

  async purchaseCompleted(payment: ProcessPaymentResult) {
    await Promise.allSettled([
      this.events.purchase().send(
        this.buildCartProperties<PurchaseCompletedData>({
          rid: payment.data?.checkout?.customer?.id,
          txid: payment.data?.checkout?.transaction?.id,
        }),
        this.eventId,
      ),
      Iterable.purchaseComplete(payment), // These parameters should be in the
      Iterable.customEvent('FunnelComplete')
    ])
  }

  async userClick(fields: UserClickFields) {
    await this.events.userClick().send(this.buildCustomProperties(fields), this.eventId)
  }

  async initiateCheckout() {
    await this.events.initiateCheckout().send(this.buildCartProperties(), this.eventId)
  }

  async purchaseAttempted() {
    await this.events.transactionAttempted().send(this.buildCustomProperties(), this.eventId)
  }

  async purchaseFailed() {

    await Promise.allSettled([
      this.events.transactionFailed().send(this.buildCustomProperties(), this.eventId),
      Iterable.customEvent('PurchaseFailed')
    ])
  }

  async upsellViewed() {
    await this.events.upsellViewed().send(this.buildCustomProperties(), this.eventId)
  }

  async trialStarted() {
    await this.events.trialStarted().send(this.buildCustomProperties(), this.eventId)
  }

  async upsellUnavailable() {
    await this.events.upsellUnavailable().send(this.buildCustomProperties(), this.eventId)
  }

  async upsellAccepted() {
    await this.events.upsellAccepted().send(this.buildCustomProperties(), this.eventId)
  }

  async upsellSucceeded() {
    await Promise.allSettled([
      this.events.purchase().send(this.buildUpsellProperties(), this.eventId),
      this.events.upsellSucceeded().send(this.buildCustomProperties(), this.eventId),
    ])
  }

  async upsellFailed() {
    await this.events.upsellFailed().send(this.buildCustomProperties(), this.eventId)
  }

  async viewContent(fields: ViewContentFields) {
    await this.events.viewContent().send(this.buildCustomProperties(fields), this.eventId)
  }

  async testStarted(fields: TestStartedFields) {
    await this.events.testStarted().send(this.buildCustomProperties(fields), this.eventId)
  }

  async funnelStarted(fields: FunnelStartedFields) {
    await this.events.funnelStarted().send(this.buildCustomProperties(fields), this.eventId)
  }

  async emailCollected(fields: { email: string }) {
    await this.events.emailCollected().send(this.buildCustomProperties(fields), this.eventId)
  }

  async accountCreated() {
    await this.events.accountCreated().send(this.buildCustomProperties(), this.eventId)
  }

  async shareCompleted() {
    await this.events.shareCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async buyticketsCompleted() {
    await this.events.buyticketsCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async installCompleted() {
    await this.events.installCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async funnelCompleted() {
    await this.events.funnelCompleted().send(this.buildCustomProperties(), this.eventId)
  }

  async offerRequested(fields: { id: string }) {
    await this.events.offerRequested().send(this.buildCustomProperties(fields), this.eventId)
  }

  async feedbackScoreCollected(fields: { score: string | number }) {
    await this.events.feedbackScoreCollected().send(this.buildCustomProperties(fields), this.eventId)
  }

  async feedbackSourceCollected(fields: { source: string }) {
    await this.events.feedbackSourceCollected().send(this.buildCustomProperties(fields), this.eventId)
  }

  async goodFeedbackRequested() {
    await this.events.goodFeedbackRequested().send(this.buildCustomProperties(), this.eventId)
  }

  async badFeedbackRequested() {
    await this.events.badFeedbackRequested().send(this.buildCustomProperties(), this.eventId)
  }

  async customEvent(name: string, data: T) {
    await this.events.customEvent(name).send(this.buildFunnelProperties(data), this.eventId)
  }

  buildCartProperties<T extends CustomProperties>(data?: T) {
    const content_ids: string[] = []
    const contents: unknown[] = []

    const cart = Cart.get()
    for (const item of cart?.getItems() ?? []) {
      content_ids.push(item.planId)
      contents.push({
        id: item.planId,
        quantity: 1,
      })
    }

    return {
      ...data,
      ...this.buildValueProperties<T>(data),
      ...this.buildContentProperties<T>(data),
      content_ids,
      contents,
      num_items: content_ids.length,
    }
  }

  buildUpsellProperties<T extends CustomProperties>(data?: T) {
    const content_ids: string[] = []
    const contents: unknown[] = []

    const cartItems = Cart.get().getSetItems()
    const item = cartItems.get(CartItemSlot.Upsell)
    if (item) {
      content_ids.push(item.planId)
      contents.push({
        id: item.planId,
        quantity: 1,
      })
    }

    return {
      ...data,
      ...this.buildContentProperties<T>(data),
      content_ids,
      contents,
      currency: 'USD',
      num_items: content_ids.length,
      value: item?.value ?? 0,
    }
  }
  buildFunnelProperties<T>(data?: T) {
    const funnel = Funnel.get().data
    const testData = new TestData().data
    return {
      ...data,
      ...funnel,
      testData
    }
  }
  buildContentProperties<T extends CustomProperties>(data?: T) {
    const cart = Cart.get()
    const funnel = Funnel.get().data
    return {
      ...funnel,
      ...data,
      ...this.buildCustomProperties<T>(data),
      content_name: cart.getItemSet(),
      content_type: 'product',
    }
  }

  buildValueProperties<T extends CustomProperties>(data?: T) {
    const cart = Cart.get()
    return {
      ...data,
      ...this.buildCustomProperties<T>(data),
      currency: 'USD',
      value: cart.getValue(),
    }
  }

  buildCustomProperties<T>(data?: T): T | CustomProperties {
    const cart = Cart.get()
    const testData = new TestData().data
    return {
      ...data,
      itemSet: cart.getItemSet(),
      testData,
    }
  }
}
